import { useKontentHomeConfig } from '@/components/contexts/KontentHomeConfigProvider'
import Link from '@/components/Link'
import { useIframeSide } from '@/hooks/useIframe'
import { useSideBySideSupport } from '@/hooks/useSideBySideSupport'
import { IPropWithClassNameChildren } from '@/types'
import { TOverarchingLinkProps } from '@/utils/getLinksFromOverarchingLinks'
import clsx from 'clsx'
import { ReactNode } from 'react'

export interface ContentOverarchingLinksProps
	extends IPropWithClassNameChildren {
	links: TOverarchingLinkProps[]
	pretitle?: ReactNode
}

export const ContentOverarchingLinks = ({
	className,
	links,
	pretitle = 'Related',
}: ContentOverarchingLinksProps) => {
	const { isInIframe } = useKontentHomeConfig()
	const iframeSide = useIframeSide()
	const isViewSideBySideSupported = useSideBySideSupport()
	if (!links || (links && links.length === 0)) return null

	return (
		<div className={clsx('flex gap-4', className)}>
			{pretitle && (
				<span className="bold flex-shrink-0 ContentOverarchingLinks__pretitle">
					{pretitle}
				</span>
			)}
			<div className="ContentOverarchingLinks__items flex flex-col gap-2">
				{links.filter(Boolean).map((link, index, _links) => {
					const olItem = link.overarchingLinkItem
					const isHashedLink = !!link.href?.toString().includes('#')
					return (
						<span
							key={olItem.system.codename}
							className={clsx(
								// index < _links.length - 1 &&
								// 	'border-r pr-2 mr-2',
								'ContentOverarchingLinks__item',
								'flex gap-2',
							)}
						>
							{isHashedLink ? (
								<a href={link.href as string}>
									{olItem.elements.title.value}
								</a>
							) : (
								<Link
									href={link.href}
									shallow={false}
									scroll={true}
								>
									{olItem.elements.title.value}
								</Link>
							)}
							{/* View side-by-side link */}
							{olItem.elements.enable_compare_view__enable_compare_view.value.some(
								(t) => t.codename === 'yes',
							) &&
								isViewSideBySideSupported && (
									<>
										<span className="inline-flex ml-[10px] align-sub">
											<div className="w-[1px] h-[24px] bg-gray-400 box-content" />
											<button
												onClick={() => {
													// if it's inside the iframe, I want to post message to the parent
													// to open the right side
													if (isInIframe) {
														if (iframeSide) {
															window.parent.postMessage(
																{
																	type: `${
																		iframeSide ===
																		'left'
																			? 'right'
																			: 'left'
																	}UrlChange`,
																	url: link.href,
																},
																'*',
															)
														}
													} else {
														// left url is current url without protocol and host, just the pathname, query string and hash
														const leftUrl =
															window.location
																.pathname +
															window.location
																.search +
															window.location.hash

														// left is current page, right is the target
														window.location.href = `/view-side-by-side?l=${encodeURIComponent(
															leftUrl,
														)}&r=${encodeURIComponent(
															link.href.toString(),
														)}`
													}
												}}
												aria-label="Open in View side-by-side"
												className={clsx(
													'inline-block ml-1 text-nsw-brand-dark w-8',
												)}
												onFocus={(e) => {
													e.target.style.outline =
														'none'
													e.target.style.boxShadow =
														'0 0 0 3px var(--nsw-focus)'
												}}
												onBlur={(e) => {
													e.target.style.boxShadow =
														'none'
												}}
											>
												<svg
													width="24"
													height="24"
													viewBox="0 0 24 24"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														d="M4 20C3.45 20 2.97917 19.8042 2.5875 19.4125C2.19583 19.0208 2 18.55 2 18V6C2 5.45 2.19583 4.97917 2.5875 4.5875C2.97917 4.19583 3.45 4 4 4H9V6H4V18H9V20H4ZM11 22V2H13V4H20C20.55 4 21.0208 4.19583 21.4125 4.5875C21.8042 4.97917 22 5.45 22 6V18C22 18.55 21.8042 19.0208 21.4125 19.4125C21.0208 19.8042 20.55 20 20 20H13V22H11ZM13 18H20V6H13V18Z"
														fill="currentColor"
													/>
												</svg>
											</button>
										</span>
									</>
								)}
						</span>
					)
				})}
			</div>
		</div>
	)
}
